import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Trash2, Image as ImageIcon, Power, EyeOff } from "lucide-react";
import { adminApi, api } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

const BackgroundsPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", image_url: "", cost: 300, description: "" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const all = await api.listShop();
      setItems(all.filter((i) => (i.type || "").toLowerCase() === "background"));
    } catch (err) { toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    if (!form.name.trim() || !form.image_url.trim()) {
      toast({ title: "Name + Bild-URL benoetigt", variant: "destructive" });
      return;
    }
    setCreating(true);
    try {
      await a.createBackground({ ...form, cost: parseInt(form.cost || 300, 10) });
      setForm({ name: "", image_url: "", cost: 300, description: "" });
      toast({ title: "Hintergrund angelegt" }); await load();
    } catch (err) { toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" }); }
    finally { setCreating(false); }
  };

  const toggleAvailable = async (it) => {
    try {
      await a.updateShopItem(it.id, { available: !it.available });
      toast({ title: it.available ? "Versteckt" : "Aktiv" });
      await load();
    } catch (err) { toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" }); }
  };

  const onDelete = async (it) => {
    if (!window.confirm(`Hintergrund "${it.name}" loeschen?`)) return;
    try { await a.deleteShopItem(it.id); toast({ title: "Geloescht" }); await load(); }
    catch (err) { toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" }); }
  };

  return (
    <div data-testid="backgrounds-panel" className="space-y-4">
      <div>
        <h2 className="flex items-center gap-2 text-xl font-bold text-white">
          <ImageIcon className="h-5 w-5 text-[#b39bff]" /> Profil-Hintergruende
        </h2>
        <p className="mt-1 text-sm text-zinc-400">
          Lege neue Profil-Hintergruende fuer den Shop an. Tipp: Du kannst Bild-URLs aus News-Header-Bannern wiederverwenden.
        </p>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5">
        <div className="text-sm font-semibold text-white">Neuen Hintergrund anlegen</div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_110px_auto]">
          <Input data-testid="bg-new-name" placeholder="Name (z.B. 'Pixel-Stadt')" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#10141b] border-white/10 text-zinc-200" />
          <Input data-testid="bg-new-url" placeholder="Bild-URL (z.B. Header-Banner)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-[#10141b] border-white/10 text-zinc-200" />
          <Input type="number" placeholder="Punkte" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="bg-[#10141b] border-white/10 text-zinc-200" />
          <Button onClick={onCreate} disabled={creating} className="btn-primary rounded-lg px-4 text-sm" data-testid="bg-new-submit">
            {creating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />} Anlegen
          </Button>
        </div>
        <div className="mt-3">
          <Textarea placeholder="Beschreibung (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="bg-[#10141b] border-white/10 text-zinc-200" />
        </div>
        {form.image_url && (
          <div className="mt-3">
            <div className="mb-1 text-[11px] uppercase tracking-wider text-zinc-500">Live-Vorschau</div>
            <div className="overflow-hidden rounded-lg border border-white/10">
              <img src={form.image_url} alt="preview" className="h-32 w-full object-cover" onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-[#0f141b] p-10 text-center text-sm text-zinc-500">
          Noch keine Profil-Hintergruende angelegt. Erstelle den ersten oben.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`bg-item-${it.slug}`} className="overflow-hidden rounded-xl border border-white/10 bg-[#0f141b]">
              <div className="relative h-36 bg-[#0a0d12]">
                <img src={it.image_url} alt={it.name} className="h-full w-full object-cover" />
                {!it.available && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-xs uppercase tracking-wider text-rose-300">
                    <EyeOff className="mr-1 h-4 w-4" /> Versteckt
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="text-sm font-semibold text-white">{it.name}</div>
                <div className="mt-0.5 flex items-center justify-between text-xs">
                  <span className="text-amber-300">{it.cost} Punkte</span>
                  <span className="text-zinc-500 font-mono text-[10px]">{it.slug}</span>
                </div>
                {it.description && <div className="mt-1 line-clamp-2 text-[11px] text-zinc-400">{it.description}</div>}
                <div className="mt-3 flex items-center justify-end gap-1">
                  <Button onClick={() => toggleAvailable(it)} className="btn-ghost rounded-lg px-2 py-1 text-xs" data-testid={`bg-toggle-${it.slug}`}>
                    <Power className="mr-1 h-3.5 w-3.5" /> {it.available ? "Verstecken" : "Aktivieren"}
                  </Button>
                  <Button onClick={() => onDelete(it)} className="btn-ghost rounded-lg px-2 py-1 text-xs text-rose-300 hover:border-rose-500/40" data-testid={`bg-delete-${it.slug}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BackgroundsPanel;
